import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import AuthFooter from '../components/shared/AuthFooter';
import { bcIcon } from '../lib/businessCategories';

const CATS = ['Fabric', 'Ladies Wear', "Men's Wear", 'Girls Wear', 'Boys Wear', 'Furnishing'];

// Standard 15-char Indian GSTIN pattern. Matches the backend regex.
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    name: '', email: '', phone: '', password: '', gst_number: '',
    business_name: '', business_type: 'Wholesaler', business_city: '',
    business_categories: [],
    business_address: { line1: '', line2: '', area: '', city: '', state: '', pincode: '', country: 'India' },
    // Affiliate referral — captured from ?ref=CODE on the referral link/QR.
    referral_code: (() => { try { return new URLSearchParams(window.location.search).get('ref') || undefined; } catch { return undefined; } })(),
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setAddr = (k, v) => setData((d) => ({ ...d, business_address: { ...d.business_address, [k]: v } }));
  const toggleCat = (c) => setData((d) => ({
    ...d,
    business_categories: d.business_categories.includes(c) ? d.business_categories.filter(x => x !== c) : [...d.business_categories, c],
  }));

  // Step-1 validation — GST is now REQUIRED (Feb 2026 spec) and must match GSTIN format.
  const gstClean = (data.gst_number || '').trim().toUpperCase().replace(/\s+/g, '');
  const gstValid = GSTIN_RE.test(gstClean);
  const addr = data.business_address;
  const pinValid = /^\d{6}$/.test((addr.pincode || '').trim());
  const addrComplete = addr.line1.trim() && addr.area.trim() && addr.city.trim()
    && addr.state.trim() && pinValid && addr.country.trim();
  const step1Complete = data.name.trim() && data.email.trim() && data.phone.trim()
    && data.password.trim().length >= 8 && gstValid && addrComplete;

  const onNext = () => {
    if (step === 1) {
      if (!data.name.trim()) return toast.error('Full name is required');
      if (!data.email.trim()) return toast.error('Email is required');
      if (!data.phone.trim()) return toast.error('Mobile number is required');
      if ((data.password || '').length < 8) return toast.error('Password must be at least 8 characters');
      if (!data.gst_number.trim()) return toast.error('GST Number is required to continue');
      if (!gstValid) return toast.error('Enter a valid 15-character GSTIN (e.g. 22AAAAA0000A1Z5)');
      if (!addr.line1.trim()) return toast.error('Address Line 1 is required');
      if (!addr.area.trim()) return toast.error('Area / Locality is required');
      if (!addr.city.trim()) return toast.error('City is required');
      if (!addr.state.trim()) return toast.error('State is required');
      if (!pinValid) return toast.error('Enter a valid 6-digit PIN Code');
      if (!addr.country.trim()) return toast.error('Country is required');
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!data.business_categories.length) {
      toast.error('Select at least one business category to continue');
      return;
    }
    setLoading(true);
    try {
      await signup(data);
      toast.success('Account created!');
      nav('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    }
    setLoading(false);
  };

  const totalSteps = 3;

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-xl fade-up">
        <Link to="/" className="flex items-center justify-center mb-5 sm:mb-6">
          <BrandLogo height={38} />
        </Link>
        <div className="fab-card p-5 sm:p-8">
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 h-1.5 rounded-full" style={{ background: s <= step ? '#6C5CE7' : '#EAE8FF' }} />
            ))}
          </div>
          <div className="text-[11px] text-fab-text-muted font-semibold uppercase tracking-wider">Step {step} of {totalSteps}</div>

          {step === 1 && (
            <div className="mt-3">
              <h2 className="font-display text-2xl font-bold text-fab-text">Create your account</h2>
              <p className="text-[13px] text-fab-text-secondary mt-1">15-day free trial. No card required.</p>
              <div className="space-y-3 mt-5">
                <input value={data.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" data-testid="signup-name" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                <input value={data.email} onChange={(e) => set('email', e.target.value)} placeholder="Email" data-testid="signup-email" type="email" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                <input value={data.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Mobile number" data-testid="signup-phone" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                
<input value={data.password} onChange={(e) => set('password', e.target.value)} placeholder="Password" data-testid="signup-password" type="password" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
<p className="text-[11px] text-fab-text-muted mt-1">
  Password must be at least 8 characters long.
</p>

                <div>
                  <input
                    value={data.gst_number}
                    onChange={(e) => set('gst_number', e.target.value.toUpperCase())}
                    placeholder="GST Number (e.g. 22AAAAA0000A1Z5)"
                    data-testid="signup-gst"
                    maxLength={15}
                    aria-required="true"
                    className={`fab-input w-full px-3.5 py-2.5 rounded-lg border text-[14px] font-mono uppercase tracking-wide ${
                      data.gst_number && !gstValid
                        ? 'border-red-400 focus:border-red-500'
                        : 'border-fab-primary-lighter'
                    }`}
                  />
                  <div className="text-[11px] text-fab-text-muted mt-1">
                    <span className="text-red-500 font-bold">*</span> Required. 15-character GSTIN as printed on your GST certificate.
                    {data.gst_number && !gstValid && (
                      <span className="text-red-500 font-semibold ml-1" data-testid="signup-gst-error">Invalid GSTIN format.</span>
                    )}
                  </div>
                </div>

                {/* Business Address — canonical seller/invoice address (Jun 2026) */}
                <div className="pt-2" data-testid="signup-business-address">
                  <div className="text-[12px] font-semibold text-fab-text-secondary mb-2 uppercase tracking-wide">Business Address</div>
                  <div className="space-y-3">
                    <input value={addr.line1} onChange={(e) => setAddr('line1', e.target.value)} placeholder="Address Line 1 *" data-testid="signup-addr-line1" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                    <input value={addr.line2} onChange={(e) => setAddr('line2', e.target.value)} placeholder="Address Line 2 (optional)" data-testid="signup-addr-line2" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                    <input value={addr.area} onChange={(e) => setAddr('area', e.target.value)} placeholder="Area / Locality *" data-testid="signup-addr-area" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={addr.city} onChange={(e) => setAddr('city', e.target.value)} placeholder="City *" data-testid="signup-addr-city" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                      <input value={addr.state} onChange={(e) => setAddr('state', e.target.value)} placeholder="State *" data-testid="signup-addr-state" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <input value={addr.pincode} onChange={(e) => setAddr('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="PIN Code *" data-testid="signup-addr-pincode" inputMode="numeric" maxLength={6} className={`fab-input w-full px-3.5 py-2.5 rounded-lg border text-[14px] ${addr.pincode && !pinValid ? 'border-red-400 focus:border-red-500' : 'border-fab-primary-lighter'}`} />
                        {addr.pincode && !pinValid && (<div className="text-[11px] text-red-500 font-semibold mt-1" data-testid="signup-addr-pincode-error">Enter a 6-digit PIN.</div>)}
                      </div>
                      <input value={addr.country} onChange={(e) => setAddr('country', e.target.value)} placeholder="Country *" data-testid="signup-addr-country" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                    </div>
                  </div>
                  <div className="mt-1 text-[11px] text-fab-text-muted">This is your official business address used on invoices.</div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-3">
              <h2 className="font-display text-2xl font-bold text-fab-text">Business profile</h2>
              <p className="text-[13px] text-fab-text-secondary mt-1">Tell us about your textile business.</p>
              <div className="space-y-3 mt-5">
                <input value={data.business_name} onChange={(e) => set('business_name', e.target.value)} placeholder="Business name" data-testid="signup-business-name" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                <select value={data.business_type} onChange={(e) => set('business_type', e.target.value)} className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px] bg-white">
                  <option>Wholesaler</option><option>Manufacturer</option><option>Trader</option><option>Retailer</option>
                </select>
                <input value={data.business_city} onChange={(e) => set('business_city', e.target.value)} placeholder="Business city (e.g. Surat)" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
                <div>
                  <div className="text-[12px] font-semibold text-fab-text-secondary mb-2">Product categories <span className="text-red-500">*</span></div>
                  <div className="flex flex-wrap gap-2" data-testid="signup-business-categories">
                    {CATS.map((c) => (
                      <button type="button" key={c} onClick={() => toggleCat(c)} data-testid={`signup-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${data.business_categories.includes(c) ? 'bg-fab-primary text-white border-fab-primary' : 'bg-white text-fab-text-secondary border-fab-primary-lighter hover:bg-fab-elevated'}`}>
                        <span className="mr-1">{bcIcon(c)}</span>{c}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-fab-text-muted">Choose one or more. Drives your product upload categories.</div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mt-3">
              <h2 className="font-display text-2xl font-bold text-fab-text">You&apos;re all set 🎉</h2>
              <p className="text-[13px] text-fab-text-secondary mt-1">Your PRO plan free trial starts now. Connect WhatsApp & upload your catalog after signing in.</p>
              <div className="mt-5 p-5 rounded-xl bg-fab-primary-lighter">
                <div className="flex items-center gap-2 text-fab-primary text-[13px] font-bold"><CheckCircle2 className="w-5 h-5" /> PRO Plan — 15 Days Free</div>
                <ul className="text-[12px] text-fab-text-secondary mt-2 space-y-1">
                  <li>• Up to 3 WhatsApp Numbers · Multi-channel AI Chats</li>
                  <li>• AI Auto-Replies · Hot Lead Detection</li>
                  <li>• Auto Catalog Broadcasting · OCR & Image Search</li>
                </ul>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-7">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="px-4 py-2.5 text-[13px] font-semibold text-fab-text-secondary flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
            ) : <Link to="/login" className="text-[13px] text-fab-text-secondary font-semibold">Already have account?</Link>}
            {step < totalSteps ? (
              <button onClick={onNext} disabled={step === 1 && !step1Complete} data-testid="signup-next" className="btn-primary text-[13px] flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">Continue <ArrowRight className="w-4 h-4" /></button>
            ) : (
              <button onClick={submit} disabled={loading} data-testid="signup-submit" className="btn-primary text-[13px]">{loading ? 'Creating...' : 'Create account'}</button>
            )}
          </div>
        </div>
        <AuthFooter />
      </div>
    </div>
  );
}
